import React, { useState, useEffect } from 'react';
import Select from 'react-select';
import {
  Filter,
  ChevronDown,
  FileSpreadsheet,
  CheckCircle,
  AlertCircle,
  XCircle
} from 'lucide-react';
import { Button } from '../../components/ui/Button';
import { getPartnersList, getSettlements } from '../../services/reports';
import ReportDateRangeEnforcer from '../../components/ReportDateRangeEnforcer';
import { useAppSelector } from '../../store/hooks/reduxHooks';
import { UserRole } from '../../types/auth';
import MerchantDataReportPage from './MerchantDataReport';

// Types
interface Settlement {
  id: string;
  transactionRef: string;
  merchantName: string;
  brandName: string;
  amount: number;
  status: 'completed' | 'pending' | 'failed';
  createdAt: string;
  completedAt?: string;
  utr?: string;
  paymentMethod: string;
}

interface SettlementFilters {
  dateRange: {
    from: string | null;
    to: string | null;
  };
  brand: string;
  merchant: string;
  merchantBank: boolean;
  merchantgst: boolean;
  lender: string; // 👈 added
}

interface SelectOption {
  value: string;
  label: string;
}
const lenderOptions: SelectOption[] = [
  { value: 'cashe', label: 'Cashe' },
  { value: 'zype', label: 'Zype' }
];


const SettlementReportPage: React.FC = () => {
  const [settlements] = useState<Settlement[]>([]);

  // Filter state
  const [filters, setFilters] = useState<SettlementFilters>({
    dateRange: {
      from: null,
      to: null
    },
    brand: '',
    merchant: '',
    merchantBank: false,
    merchantgst: false,
    lender: '' // 👈 init empty
  });
  const [isExporting, setIsExporting] = useState(false);
  const [exportFormat] = useState<'excel'>('excel');

  // Available options for filters
  const [brandOptions, setBrandOptions] = useState<SelectOption[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isFilterApplied, setIsFilterApplied] = useState(false);
  const { user } = useAppSelector(state => state.auth);

  // Fetch partners
  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      try {
        const partners = await getPartnersList();

        // Convert to react-select options format
        let brandOpts = partners.map(name => ({ value: name, label: name }));

        // Filter partners for CleverPe users based on their groups
        if (user?.role === UserRole.CLEVERPE_USER && user?.groups && user.groups.length > 0) {
          brandOpts = brandOpts.filter(option => user.groups?.includes(option.value));
        }

        setBrandOptions(brandOpts);
      } catch (error) {
        console.error('Error fetching options:', error);
        setBrandOptions([]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [user]);

  useEffect(() => {
    // Check if filter is applied
    const hasDateRange = filters.dateRange.from && filters.dateRange.to;
    const hasOtherFilters = filters.brand || filters.merchant;

    setIsFilterApplied(!!hasDateRange && !!hasOtherFilters);
  }, [filters]);

  // Format currency
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0,
    }).format(amount);
  };

  // Handle filter changes
  const handleFilterChange = (key: keyof SettlementFilters, value: any) => {
    if (key === 'dateRange') {
      setFilters(prev => ({
        ...prev,
        dateRange: {
          ...prev.dateRange,
          ...value
        }
      }));
    } else {
      setFilters(prev => ({
        ...prev,
        [key]: value
      }));
    }
  };

  // Handle filter reset
  const handleResetFilters = () => {
    setFilters({
      dateRange: {
        from: null,
        to: null
      },
      brand: '',
      merchant: '',
      merchantBank: false,
      merchantgst: false,
      lender: '' // 👈 reset

    });
  };

  // Handler to export report data
  const handleExportData = async (format: 'excel') => {
    if (format !== 'excel') return;

    setIsExporting(true);
    setError(null); // Clear previous errors

    try {
      // Validate dates
      if (filters.dateRange.from && filters.dateRange.to) {
        const fromDate = new Date(filters.dateRange.from);
        const toDate = new Date(filters.dateRange.to);
        if (fromDate > toDate) {
          setError('From date cannot be later than To date');
          return;
        }
      }

      const params = {
        partner: filters.brand || undefined,
        merchant: filters.merchant || undefined,
        from: filters.dateRange.from || undefined,
        to: filters.dateRange.to || undefined,
        merchantbank: filters.merchantBank ? true : undefined,
        merchantgst: filters.merchantgst ? true : undefined,
        lender: filters.lender || undefined // 👈 send lowercase
      };

      // CleverPe User validation
      if (user?.role === UserRole.CLEVERPE_USER) {
        const hasDateRange = filters.dateRange.from && filters.dateRange.to;
        const hasOtherFilters = filters.brand || filters.merchant;

        if (!hasDateRange) {
          setError('Date range is required for CleverPe users');
          return;
        }

        if (!hasOtherFilters) {
          setError('At least one additional filter is required for CleverPe users');
          return;
        }
      } else if (!params.partner && !params.merchant && !params.from && !params.to) {
        setError('Please select at least one filter');
        return;
      }

      const blob = await getSettlements(params);

      // Create a download link for the Excel file
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `settlements-${new Date().toISOString()}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

    } catch (error: any) {
      console.error('Error exporting data:', error);
      setError(error?.message || 'Failed to export data. Please try again.');
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <div className={`w-full space-y-6 ${isLoading ? 'opacity-50 pointer-events-none' : ''}`}>
      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border-l-4 border-red-400 p-4 rounded-md animate-in fade-in slide-in-from-top duration-300">
          <div className="flex items-center">
            <AlertCircle className="h-5 w-5 text-red-400 mr-2" />
            <p className="text-sm text-red-700">{error}</p>
            <button
              onClick={() => setError(null)}
              className="ml-auto text-red-400 hover:text-red-500"
            >
              <XCircle className="h-5 w-5" />
            </button>
          </div>
        </div>
      )}

      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center space-y-4 sm:space-y-0">
        <div>
          {/* Header title removed - handled by Header component */}
        </div>

        <div className="flex space-x-3">
          <Button
            isLoading={isExporting && exportFormat === 'excel'}
            onClick={() => handleExportData('excel')}
            className="flex items-center"
          >
            <FileSpreadsheet size={16} className="mr-1.5" />
            {isExporting && exportFormat === 'excel' ? 'Downloading...' : 'Export Excel'}
          </Button>
        </div>
      </div>

      {/* Filter panel - Always visible */}
      <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
        <h2 className="text-lg font-gilroy-semibold text-gray-800 mb-4">Settlement Report Filters</h2>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Date range filters */}
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">Date Range</label>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="block text-xs text-gray-500 mb-1">From</label>
                <input
                  type="date"
                  value={filters.dateRange.from || ''}
                  onChange={(e) => handleFilterChange('dateRange', { from: e.target.value || null })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">To</label>
                <input
                  type="date"
                  value={filters.dateRange.to || ''}
                  onChange={(e) => handleFilterChange('dateRange', { to: e.target.value || null })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                />
              </div>
            </div>
          </div>

          {/* Brand filter */}
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">Partner/Brand</label>
            <Select
              isClearable
              isSearchable
              isLoading={isLoading}
              value={brandOptions.find(opt => opt.value === filters.brand)}
              onChange={(selected) => handleFilterChange('brand', selected?.value || '')}
              options={brandOptions}
              className="text-sm"
              placeholder="Search partners/brands..."
              noOptionsMessage={() => "No partners found"}
            />
          </div>

          {/* Merchant filter */}
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">Merchant</label>
            <input
              type="text"
              value={filters.merchant}
              onChange={(e) => handleFilterChange('merchant', e.target.value)}
              placeholder="Enter merchant ID"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
            />
          </div>
          {/* Lender filter */}
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">Lender</label>
            <Select
              isClearable
              isSearchable={false}
              value={lenderOptions.find(opt => opt.value === filters.lender)}
              onChange={(selected) => handleFilterChange('lender', selected?.value || '')}
              options={lenderOptions}
              className="text-sm"
              placeholder="Select lender..."
              noOptionsMessage={() => "No lenders available"}
            />
          </div>
          {/* Include Merchant Bank Details checkbox */}
          <div className="space-y-2 md:col-span-3">
            <label className="inline-flex items-center mt-2">
              <input
                type="checkbox"
                checked={filters.merchantBank}
                onChange={(e) => handleFilterChange('merchantBank', e.target.checked)}
                className="form-checkbox h-4 w-4 text-blue-600"
              />
              <span className="ml-2 text-sm text-gray-700">Include Merchant Bank Details</span>
            </label>
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={filters.merchantgst}
                onChange={(e) => handleFilterChange('merchantgst', e.target.checked)}
                className="form-checkbox h-4 w-4 text-blue-600"
              />
              <span className="ml-2 text-sm text-gray-700">Include Merchant GST No</span>
            </label>
          </div>
        </div>

        {/* Filter actions */}
        <div className="flex justify-end mt-6 space-x-3">
          <Button
            variant="outline"
            onClick={handleResetFilters}
            size="sm"
          >
            Reset Filters
          </Button>
          <Button
            onClick={() => handleExportData('excel')}
            size="sm"
          >
            Apply Filters
          </Button>
        </div>
      </div>

      {/* Wrap the export section with ReportDateRangeEnforcer */}
      <ReportDateRangeEnforcer
        dateRange={{
          startDate: filters.dateRange.from ? new Date(filters.dateRange.from) : null,
          endDate: filters.dateRange.to ? new Date(filters.dateRange.to) : null
        }}
        isFilterApplied={isFilterApplied}
      >
        {/* Export section */}
        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-6 border border-blue-200">
          <div className="flex flex-col md:flex-row justify-between items-center">
            <div className="mb-4 md:mb-0">
              <h3 className="text-lg font-gilroy-bold text-gray-800">Export Settlement Report</h3>
              <p className="text-gray-600 mt-1">
                {isExporting ? 'Preparing your download...' :
                  error ? 'Please check the filters and try again' :
                    'Download the complete settlement report with all applied filters'}
              </p>
            </div>
            <div className="flex space-x-3">
              <Button
                isLoading={isExporting}
                onClick={() => handleExportData('excel')}
                disabled={isLoading || Boolean(error)}
                className="flex items-center"
              >
                <FileSpreadsheet size={16} className="mr-1.5" />
                Excel
              </Button>
            </div>
          </div>
        </div>
      </ReportDateRangeEnforcer>
    </div >
  );
};

export default SettlementReportPage;